/**
 * SizeControl — size chip/menu shown in edit mode for tiles that have
 * more than one allowed size.
 *
 * Lists ONLY the registry-allowed sizes for the tile type (passed in as
 * allowedSizes). The current size is visually active. Selecting a different
 * size fires onResize; the caller (TileCell → BoardGrid → App) calls
 * commitResize which validates via resizeTile (board-ops).
 *
 * The 'invalid-size' error can never happen through this UI because the
 * menu is constrained to allowedSizes — but commitResize handles it
 * calmly if it ever does.
 *
 * Blur-close fix: the previous implementation used a 120ms setTimeout on
 * blur to allow item clicks to register before closing. This is a fragile
 * timing hack that breaks on slow devices. Replaced with an outside
 * pointerdown listener (attached when open, removed when closed). Selection
 * close (item click) calls setOpen(false) directly — no timer needed.
 *
 * Immutability: functional component, no mutation of props.
 * glance-grammar-spec §2.1: three sizes only (1×1, 2×1, 2×2).
 */
import { useState, useEffect, useRef } from 'react';
import type { TileSize } from 'briefpane/schema';

const SIZE_LABELS: Record<string, string> = {
  '1x1': '1×1',
  '2x1': '2×1',
  '1x2': '1×2',
  '2x2': '2×2',
  '3x2': '3×2',
};

interface SizeControlProps {
  readonly currentSize: TileSize;
  readonly allowedSizes: readonly TileSize[];
  readonly onResize: (size: TileSize) => void;
  readonly tileLabel: string;
}

/**
 * Renders a compact chip bar showing the allowed sizes.
 * The active size chip is visually distinguished (filled style).
 * Positioned at bottom-left of the tile, above the remove button (z-index 11).
 */
export function SizeControl({ currentSize, allowedSizes, onResize, tileLabel }: SizeControlProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside pointerdown — no setTimeout hack.
  // The listener is only attached while the menu is open (performance), and
  // removed on close or unmount. Using 'pointerdown' (not 'click') so the
  // menu closes before the item's click handler fires on the external target.
  useEffect(() => {
    if (!open) return;
    function handleOutsidePointerDown(e: PointerEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', handleOutsidePointerDown, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', handleOutsidePointerDown, { capture: true });
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        bottom: 6,
        left: 6,
        zIndex: 11,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '2px',
      }}
    >
      {/* Chip button: shows current size, opens menu on click */}
      <button
        type="button"
        aria-label={`Resize ${tileLabel} tile — current size ${currentSize}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        style={{
          padding: '2px 6px',
          fontSize: '10px',
          fontWeight: 500,
          background: 'var(--color-bg-tile)',
          border: '1px solid var(--color-border)',
          borderRadius: '4px',
          cursor: 'pointer',
          color: 'var(--color-text-dim)',
          lineHeight: 1.4,
          userSelect: 'none',
        }}
      >
        {SIZE_LABELS[currentSize] ?? currentSize}
      </button>

      {/* Dropdown list: shown when open */}
      {open && (
        <ul
          role="listbox"
          aria-label={`Size options for ${tileLabel}`}
          style={{
            listStyle: 'none',
            margin: 0,
            padding: '2px',
            background: 'var(--color-bg-tile)',
            border: '1px solid var(--color-border)',
            borderRadius: '4px',
            boxShadow: 'var(--shadow-tile)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1px',
            position: 'absolute',
            bottom: '100%',
            left: 0,
            marginBottom: '2px',
            minWidth: '52px',
            zIndex: 12,
          }}
        >
          {allowedSizes.map((size) => {
            const isActive = size === currentSize;
            return (
              <li key={size} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    if (!isActive) onResize(size);
                  }}
                  style={{
                    width: '100%',
                    padding: '3px 8px',
                    fontSize: '10px',
                    fontWeight: isActive ? 600 : 400,
                    background: isActive ? 'var(--color-accent)' : 'transparent',
                    color: isActive ? '#fff' : 'var(--color-text-secondary)',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: isActive ? 'default' : 'pointer',
                    textAlign: 'left',
                    lineHeight: 1.4,
                  }}
                >
                  {SIZE_LABELS[size] ?? size}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
